import React, { useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  Button,
  IconButton,
  CircularProgress,
  Fade,
  Backdrop,
} from '@mui/material';
import {
  PhotoCamera,
  Close,
  CloudUpload,
  CheckCircle,
} from '@mui/icons-material';
import { doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../../firebaseConfig/firebase';

const PhotoUploadModal = ({ 
  open, 
  onClose, 
  patientId, 
  patientName, 
  onPhotoUploaded 
}) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const [preview, setPreview] = useState(null);
  const [compressing, setCompressing] = useState(false);

  const compressImage = (file, targetSizeKB = 250, maxWidth = 600, maxHeight = 600) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = async () => {
        // Calculate new dimensions while maintaining aspect ratio
        let { width, height } = img;
        
        if (width > height) {
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = (width * maxHeight) / height;
            height = maxHeight;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        ctx.drawImage(img, 0, 0, width, height);
        
        // Iterative compression to reach target size
        let quality = 0.9;
        let blob;
        let attempts = 0;
        const maxAttempts = 10;
        
        do {
          blob = await new Promise((blobResolve) => {
            canvas.toBlob(blobResolve, 'image/jpeg', quality);
          });
          
          const sizeKB = blob.size / 1024;
          
          if (sizeKB <= targetSizeKB || attempts >= maxAttempts) {
            break;
          }
          
          // Reduce quality more aggressively if file is still too large
          if (sizeKB > targetSizeKB * 2) {
            quality -= 0.15; // Bigger reduction for very large files
          } else {
            quality -= 0.1; // Smaller reduction as we get closer
          }
          
          // Don't go below minimum quality
          if (quality < 0.3) {
            quality = 0.3;
            break;
          }
          
          attempts++;
        } while (true);
        
        console.log(`Final compression: ${(blob.size / 1024).toFixed(1)} KB with quality ${quality.toFixed(2)}`);
        resolve(blob);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files[0];
    if (file && file.type.startsWith('image/')) {
      setCompressing(true);
      try {
        // Show original file size
        const originalSizeKB = (file.size / 1024).toFixed(1);
        console.log(`Original image size: ${originalSizeKB} KB`);
        
        // Compress the image to target size (250 KB max)
        const compressedBlob = await compressImage(file, 250, 600, 600);
        const compressedSizeKB = (compressedBlob.size / 1024).toFixed(1);
        console.log(`Compressed image size: ${compressedSizeKB} KB (${((1 - compressedBlob.size / file.size) * 100).toFixed(1)}% reduction)`);
        
        // Create a new File object from the compressed blob
        const compressedFile = new File([compressedBlob], file.name, {
          type: 'image/jpeg',
          lastModified: Date.now(),
        });
        
        setSelectedFile(compressedFile);
        
        // Create preview from compressed image
        const reader = new FileReader();
        reader.onload = (e) => setPreview(e.target.result);
        reader.readAsDataURL(compressedFile);
        
      } catch (error) {
        console.error('Error compressing image:', error);
        // Fallback to original file if compression fails
        setSelectedFile(file);
        const reader = new FileReader();
        reader.onload = (e) => setPreview(e.target.result);
        reader.readAsDataURL(file);
      } finally {
        setCompressing(false);
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !patientId) return;

    setUploading(true);
    try {
      // Upload to Firebase Storage
      const storageRef = ref(storage, `patient-photos/${patientId}_${Date.now()}`);
      const snapshot = await uploadBytes(storageRef, selectedFile);
      const photoURL = await getDownloadURL(snapshot.ref);

      // Update patient profile in Firestore
      const patientRef = doc(db, 'patientProfile', patientId);
      await updateDoc(patientRef, {
        photoURL: photoURL
      });

      setUploaded(true);
      setTimeout(() => {
        onPhotoUploaded(photoURL);
        handleClose();
      }, 1500);
    } catch (error) {
      console.error('Error uploading photo:', error);
      alert('Failed to upload photo. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedFile(null);
    setPreview(null);
    setUploading(false);
    setUploaded(false);
    setCompressing(false);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={!uploading ? handleClose : undefined}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
    >
      <Fade in={open}>
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: '90%', sm: 400 },
            bgcolor: 'background.paper',
            borderRadius: 3,
            boxShadow: 24,
            p: 0,
            overflow: 'hidden',
            animation: 'slideIn 0.3s ease-out',
            '@keyframes slideIn': {
              from: {
                opacity: 0,
                transform: 'translate(-50%, -60%) scale(0.9)',
              },
              to: {
                opacity: 1,
                transform: 'translate(-50%, -50%) scale(1)',
              },
            },
          }}
        >
          {/* Header */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, rgba(12, 17, 52, 0.9) 0%, rgba(12, 17, 52, 0.7) 100%)',
              color: 'white',
              p: 3,
              position: 'relative',
            }}
          >
            <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
              Photo Required for {patientName}
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.5 }}>
              Please upload a clear photo to proceed with OPD entry
            </Typography>
            {!uploading && (
              <IconButton
                onClick={handleClose}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: 8,
                  color: 'white',
                }}
              >
                <Close />
              </IconButton>
            )}
          </Box>

          {/* Content */}
          <Box sx={{ p: 3 }}>
            {uploaded ? (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  py: 4,
                  animation: 'bounceIn 0.6s ease-out',
                  '@keyframes bounceIn': {
                    '0%': { transform: 'scale(0.3)', opacity: 0 },
                    '50%': { transform: 'scale(1.05)' },
                    '70%': { transform: 'scale(0.9)' },
                    '100%': { transform: 'scale(1)', opacity: 1 },
                  },
                }}
              >
                <CheckCircle
                  sx={{
                    fontSize: 64,
                    color: '#4caf50',
                    mb: 2,
                  }}
                />
                <Typography variant="h6" sx={{ color: '#4caf50', fontWeight: 600 }}>
                  Photo Uploaded Successfully!
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
                  Creating OPD entry...
                </Typography>
              </Box>
            ) : (
              <>
                {/* Upload Area */}
                <Box
                  sx={{
                    border: '2px dashed #e0e0e0',
                    borderRadius: 2,
                    p: 4,
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      borderColor: 'rgba(12, 17, 52, 0.8)',
                      backgroundColor: 'rgba(12, 17, 52, 0.05)',
                    },
                    ...(preview && {
                      border: '2px solid rgba(12, 17, 52, 0.8)',
                      backgroundColor: 'rgba(12, 17, 52, 0.05)',
                    }),
                  }}
                  onClick={() => document.getElementById('photo-input').click()}
                >
                  <input
                    id="photo-input"
                    type="file"
                    accept="image/*"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />

                  {compressing ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        py: 2,
                      }}
                    >
                      <CircularProgress sx={{ mb: 2, color: 'rgba(12, 17, 52, 0.8)' }} />
                      <Typography variant="body2" sx={{ color: 'rgba(12, 17, 52, 0.8)' }}>
                        Compressing image...
                      </Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary', mt: 0.5 }}>
                        Compressing to max 250 KB
                      </Typography>
                    </Box>
                  ) : preview ? (
                    <Box
                      sx={{
                        animation: 'fadeIn 0.5s ease-in',
                        '@keyframes fadeIn': {
                          from: { opacity: 0, transform: 'scale(0.9)' },
                          to: { opacity: 1, transform: 'scale(1)' },
                        },
                      }}
                    >
                      <img
                        src={preview}
                        alt="Preview"
                        style={{
                          width: '120px',
                          height: '120px',
                          borderRadius: '50%',
                          objectFit: 'cover',
                          border: '4px solid rgba(12, 17, 52, 0.8)',
                        }}
                      />
                      <Typography variant="body2" sx={{ mt: 2, color: 'rgba(12, 17, 52, 0.8)' }}>
                        Click to change photo
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#4caf50', mt: 0.5, display: 'block' }}>
                        ✓ Optimized to ~250 KB
                      </Typography>
                    </Box>
                  ) : (
                    <Box>
                      <PhotoCamera
                        sx={{
                          fontSize: 48,
                          color: '#bdbdbd',
                          mb: 2,
                        }}
                      />
                      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                        Click to select photo
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                        Auto-compressed to max 250 KB
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Action Buttons */}
                <Box
                  sx={{
                    display: 'flex',
                    gap: 2,
                    mt: 3,
                    justifyContent: 'space-between',
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={handleClose}
                    disabled={uploading || compressing}
                    sx={{
                      borderColor: '#e0e0e0',
                      color: 'text.secondary',
                      '&:hover': {
                        borderColor: '#bdbdbd',
                      },
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleUpload}
                    disabled={!selectedFile || uploading || compressing}
                    startIcon={
                      uploading ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : compressing ? (
                        <CircularProgress size={20} color="inherit" />
                      ) : (
                        <CloudUpload />
                      )
                    }
                    sx={{
                      background: 'linear-gradient(135deg, rgba(12, 17, 52, 0.9) 0%, rgba(12, 17, 52, 0.7) 100%)',
                      '&:hover': {
                        background: 'linear-gradient(135deg, rgba(12, 17, 52, 1) 0%, rgba(12, 17, 52, 0.8) 100%)',
                      },
                      '&:disabled': {
                        background: '#e0e0e0',
                      },
                    }}
                  >
                    {uploading ? 'Uploading...' : compressing ? 'Processing...' : 'Upload & Proceed'}
                  </Button>
                </Box>
              </>
            )}
          </Box>
        </Box>
      </Fade>
    </Modal>
  );
};

export default PhotoUploadModal;
